// WHAT: Modern morning session tab component for MiracleFlow productivity workflow
// WHY:  Provides modern interface for starting, managing, and completing morning productivity sessions
// DOMAIN: Productivity → MiracleFlow → Morning Session
// DEPENDENCIES: useSession hook, ModernSessionFlow component, exercise video hook
// RISKS:
//   - NOTE: Session state is managed locally and may desync with backend
//   - WARN: Exit confirmation modal may be bypassed if session state is lost
//   - NOTE: Session duration calculation relies on local start time, not server time

import type { Session } from "../types";

import { AlertTriangle, CheckCircle, RefreshCw } from "lucide-react";
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { Card, Button, Stack, Heading } from "@/components/ui";

import { ModernSessionFlow } from "../components/organisms/ModernSessionFlow";
import { ModernSessionHeader } from "../components/organisms/ModernSessionHeader";
import { ModernSessionWelcome } from "../components/organisms/ModernSessionWelcome";
import { useExerciseVideo } from "../hooks";
import { useSession } from "../hooks/useSession";

/**
 * WHAT:
 *   Modern React component for managing morning MiracleFlow productivity sessions.
 *
 * WHY:
 *   Provides modern interface for starting, tracking, and completing morning sessions
 *   with exercise video integration and session state management.
 *
 * BEHAVIOR:
 *   - Automatically checks for active sessions on mount
 *   - Loads exercise video URL for morning sessions
 *   - Manages session state (active/inactive, start time, progress)
 *   - Handles session start, stop, and completion
 *   - Displays exit confirmation when leaving active session
 *   - Shows success message after session completion
 *
 * RISKS:
 *   - WARN: Session state is managed locally - may desync with backend
 *   - NOTE: Session duration uses local time, not synchronized with server
 *   - WARN: Exit confirmation can be bypassed if component unmounts
 *
 * @returns ModernMiracleFlowMorningSessionTab component
 *
 */
/**
 * WHAT:
 *   Checks if a session object represents an active session state.
 *
 * WHY:
 *   Validates session state across different possible string formats (case-insensitive).
 *
 * INPUTS:
 *   - session: { state?: string } | null | undefined - Session object to check
 *
 * OUTPUT:
 *   - boolean - True if session is active, false otherwise
 *
 * RISKS:
 *   - NOTE: Case-insensitive comparison handles inconsistent backend state formats
 *   - WARN: Returns false for null/undefined, may hide edge cases
 *
 * @param session
 *
 * @returns True if session is active, false otherwise
 *
 */
function isSessionActive(session: null | undefined | { state?: string }): boolean {
  return (
    session !== null &&
    session !== undefined &&
    (session.state === "Active" ||
      session.state === "active" ||
      String(session.state).toLowerCase() === "active")
  );
}

export const ModernMiracleFlowMorningSessionTab: React.FC = () => {
  const navigate = useNavigate();
  const [sessionActive, setSessionActive] = useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [localError, setLocalError] = useState<null | string>(null);
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);

  const { isLoading, refreshSession, startMorningSession, stopSession } = useSession();
  const [localSession, setLocalSession] = useState<null | Session>(null);
  const [justStoppedSession, setJustStoppedSession] = useState(false);
  const justStoppedReference = useRef(false);

  // NOTE: Load exercise video URL for morning session integration
  const { videoData: exerciseVideoData } = useExerciseVideo();

  /**
   * WHAT:
   *   Checks for active session on mount and automatically activates it if found.
   *
   * WHY:
   *   Restores session state when component mounts, allowing users to resume active sessions.
   *
   * SIDE EFFECTS:
   *   - Refreshes session from backend
   *   - Updates local session state if active session found
   *   - Sets session start time from created_at timestamp
   *   - Updates session active flag
   *
   * RISKS:
   *   - NOTE: Session start time uses created_at, not actual start time
   *   - WARN: Fails silently if refreshSession throws error
   */
  useEffect(() => {
    const checkAndActivateSession = async () => {
      try {
        const refreshedSession = await refreshSession();

        if (refreshedSession && isSessionActive(refreshedSession)) {
          setLocalSession(refreshedSession);
          setSessionActive(true);
          // NOTE: Uses created_at timestamp, not actual session start time
          if (refreshedSession.created_at) {
            const createdAt = refreshedSession.created_at;
            // NOTE: Safely parse created_at to avoid passing arbitrary values into Date constructor
            if (typeof createdAt === "string" || typeof createdAt === "number") {
              setSessionStartTime(new Date(createdAt));
            }
          }
        }
      } catch {
        setLocalError("Failed to load session");
      }
    };

    checkAndActivateSession().catch(() => {
      // Session check errors are handled in the function
    });
  }, [refreshSession]);

  /**
   * WHAT:
   *   Starts a new morning session and updates component state.
   *
   * WHY:
   *   Initiates morning productivity session workflow with exercise video integration.
   *
   * SIDE EFFECTS:
   *   - Creates new session via useSession hook
   *   - Updates local session state
   *   - Sets session start time to current time
   *   - Activates session state
   *
   * RISKS:
   *   - WARN: Session start time uses local time, not synchronized with server
   *   - NOTE: Fails silently if startMorningSession returns null
   */
  const handleStartSession = async () => {
    try {
      setLocalError(null);
      const newSession = await startMorningSession();
      if (newSession) {
        setLocalSession(newSession);
        setSessionActive(true);
        setSessionStartTime(new Date());
      }
    } catch {
      setLocalError("Failed to start morning session");
    }
  };

  /**
   * WHAT:
   *   Stops the current session and displays success message.
   *
   * WHY:
   *   Allows users to cancel or complete session early, saving progress.
   *
   * SIDE EFFECTS:
   *   - Stops session via useSession hook
   *   - Clears local session state
   *   - Resets session start time
   *   - Shows success message for 3 seconds
   *   - Updates ref to track just-stopped state
   *
   * RISKS:
   *   - NOTE: Success message auto-hides after 3 seconds
   *   - WARN: Session state cleared immediately, may lose unsaved progress
   */
  const handleStopSession = async () => {
    try {
      setLocalError(null);
      await stopSession();
      setSessionActive(false);
      setLocalSession(null);
      setSessionStartTime(null);
      setJustStoppedSession(true);
      justStoppedReference.current = true;

      // NOTE: Reset the flag after a delay to hide success message
      setTimeout(() => {
        setJustStoppedSession(false);
        justStoppedReference.current = false;
      }, 3000);
    } catch {
      setLocalError("Failed to stop session");
    }
  };

  /**
   * WHAT:
   *   Handles session completion callback from ModernSessionFlow.
   *
   * WHY:
   *   Cleans up session state when session is completed through the flow component.
   *
   * SIDE EFFECTS:
   *   - Deactivates session
   *   - Clears local session state
   *   - Resets session start time
   *
   * RISKS:
   *   - NOTE: Does not call stopSession - assumes flow component handles backend update
   */
  const handleSessionComplete = () => {
    setSessionActive(false);
    setLocalSession(null);
    setSessionStartTime(null);
  };

  /**
   * WHAT:
   *   Handles back navigation with exit confirmation for active sessions.
   *
   * WHY:
   *   Prevents accidental loss of session progress when navigating away.
   *
   * SIDE EFFECTS:
   *   - Shows exit confirmation modal if session is active
   *   - Navigates directly if no active session
   *
   * RISKS:
   *   - WARN: Exit confirmation can be bypassed if component unmounts
   */
  const handleBackToDashboard = () => {
    if (sessionActive) {
      setShowExitConfirmation(true);
    } else {
      const result = navigate("/miracle-flow");
      if (result instanceof Promise) {
        result.catch(() => {
          // Navigation errors are handled silently
        });
      }
    }
  };

  /**
   * WHAT:
   *   Confirms exit and navigates to dashboard.
   *
   * WHY:
   *   Allows user to exit active session after confirmation.
   *
   * SIDE EFFECTS:
   *   - Closes exit confirmation modal
   *   - Navigates to miracle-flow dashboard
   *
   * RISKS:
   *   - WARN: Session progress may be lost if not saved before exit
   */
  const confirmExit = () => {
    setShowExitConfirmation(false);
    const result = navigate("/miracle-flow");
    if (result instanceof Promise) {
      result.catch(() => {
        // Navigation errors are handled silently
      });
    }
  };

  /**
   * WHAT:
   *   Cancels exit and keeps session active.
   *
   * WHY:
   *   Allows user to cancel exit and continue session.
   *
   * SIDE EFFECTS:
   *   - Closes exit confirmation modal
   *   - Session remains active
   */
  const cancelExit = () => {
    setShowExitConfirmation(false);
  };

  /**
   * WHAT:
   *   Calculates and formats session duration from start time.
   *
   * WHY:
   *   Displays elapsed time since session started for user feedback.
   *
   * OUTPUT:
   *   - string | null - Formatted duration as "MM:SS" or null if no start time
   *
   * RISKS:
   *   - NOTE: Uses local time calculation, not synchronized with server
   *   - WARN: May show incorrect duration if system clock changes
   *
   * @returns Formatted duration string or null
   *
   */
  const getSessionDuration = (): string | undefined => {
    if (!sessionStartTime) {
      return undefined;
    }
    const now = new Date();
    const diff = now.getTime() - sessionStartTime.getTime();
    const minutes = Math.floor(diff / 60_000);
    const seconds = Math.floor((diff % 60_000) / 1000);
    return `${String(minutes)}:${seconds.toString().padStart(2, "0")}`;
  };

  /**
   * WHAT:
   *   Calculates session progress percentage based on completed steps.
   *
   * WHY:
   *   Provides visual feedback on morning session completion progress.
   *
   * OUTPUT:
   *   - number - Progress percentage (0-100)
   *
   * RISKS:
   *   - NOTE: Hardcoded total steps (6) for morning session - may break if structure changes
   *   - WARN: Returns 0 if no session, may cause division by zero in display
   *
   * @returns Progress percentage (0-100)
   *
   */
  const getSessionProgress = () => {
    if (!localSession) {
      return 0;
    }
    const totalSteps = 6; // NOTE: Morning session has 6 steps - hardcoded
    const completedSteps = localSession.completed_steps.length;
    return (completedSteps / totalSteps) * 100;
  };

  return (
    <div className="space-y-6">
      {/* Modern Header */}
      <ModernSessionHeader
        onBackToDashboard={handleBackToDashboard}
        progress={getSessionProgress()}
        sessionActive={sessionActive}
        sessionDuration={getSessionDuration()}
        sessionType="Morning"
      />

      {/* Error Display */}
      {localError ? (
        <Card className="bg-red-500/10 border-red-500/20" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <p className="text-red-300 text-sm">{localError}</p>
            <Button
              className="ml-auto text-red-400 hover:text-red-300"
              onClick={() => {
                setLocalError(null);
              }}
              size="sm"
              variant="ghost"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </Stack>
        </Card>
      ) : null}

      {/* Success Message */}
      {justStoppedSession ? (
        <Card className="bg-green-500/10 border-green-500/20" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <CheckCircle className="w-5 h-5 text-green-500" />
            <p className="text-green-300 text-sm">Morning session completed successfully!</p>
          </Stack>
        </Card>
      ) : null}

      {/* Session Content */}
      {sessionActive && localSession ? (
        <ModernSessionFlow
          exerciseVideoUrl={(() => {
            if (
              exerciseVideoData &&
              typeof exerciseVideoData === "object" &&
              "video_url" in exerciseVideoData
            ) {
              if (typeof exerciseVideoData.video_url === "string") {
                return exerciseVideoData.video_url;
              }
              return null;
            }
            return null;
          })()}
          onSessionCancel={() => {
            handleStopSession().catch(() => {
              // Session stop errors are handled silently
            });
          }}
          onSessionComplete={handleSessionComplete}
          session={localSession}
          sessionType="Morning"
        />
      ) : (
        <ModernSessionWelcome
          hasActiveSession={sessionActive}
          isLoading={isLoading}
          onStartSession={() => {
            handleStartSession().catch(() => {
              // Session start errors are handled silently
            });
          }}
          sessionType="Morning"
        />
      )}

      {/* Exit Confirmation Modal */}
      {showExitConfirmation ? (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card
            className="bg-slate-800 border-slate-700 max-w-md mx-4"
            padding="lg"
            variant="elevated"
          >
            <Stack direction="col" gap={4}>
              <Stack align="center" direction="row" gap={3}>
                <AlertTriangle className="w-6 h-6 text-yellow-500" />
                <Heading className="text-white" level="h3" size="md">
                  Exit Session?
                </Heading>
              </Stack>
              <p className="text-slate-300">
                You have an active session running. Are you sure you want to exit? Your progress
                will be saved.
              </p>
              <Stack className="justify-end" direction="row" gap={3}>
                <Button
                  className="border-slate-600/50 text-slate-300 hover:bg-slate-700/50"
                  onClick={cancelExit}
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  className="bg-red-600/80 hover:bg-red-600 border border-red-500/50 hover:border-red-500 text-white"
                  onClick={confirmExit}
                  variant="primary"
                >
                  Exit Session
                </Button>
              </Stack>
            </Stack>
          </Card>
        </div>
      ) : null}
    </div>
  );
};
